import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, FileCode2, Search, Send } from 'lucide-react';
import API, { formatError } from '../utils/api';
import { toast } from 'sonner';

const CATEGORIES = ['all', 'nda', 'hr', 'vendor', 'legal', 'finance', 'custom'];
const CATEGORY_COLORS = {
  nda: 'bg-purple-50 text-purple-700',
  hr: 'bg-blue-50 text-blue-700',
  vendor: 'bg-amber-50 text-amber-700',
  legal: 'bg-red-50 text-red-700',
  finance: 'bg-emerald-50 text-emerald-700',
  custom: 'bg-slate-100 text-slate-600',
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Templates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = () => {
    API.get('/templates')
      .then(r => setTemplates(r.data))
      .catch(() => toast.error('Failed to load templates'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await API.delete(`/templates/${id}`);
      toast.success('Template deleted');
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const filtered = templates.filter(t =>
    (catFilter === 'all' || t.category === catFilter) &&
    t.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 md:p-8 fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Templates</h1>
          <p className="text-slate-500 text-sm mt-0.5">{templates.length} template{templates.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => navigate('/templates/new')}
          className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium transition-colors"
          data-testid="create-template-btn"
        >
          <Plus size={15} /> New Template
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text" placeholder="Search templates..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            data-testid="template-search-input"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setCatFilter(c)}
              className={`px-3 py-1.5 rounded-sm text-xs font-medium capitalize transition-colors ${catFilter === c ? 'bg-blue-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`category-filter-${c}`}
            >
              {c === 'all' ? 'All' : c.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <FileCode2 size={36} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm">No templates found</p>
          <button onClick={() => navigate('/templates/new')} className="mt-3 text-blue-700 text-sm hover:underline">Create your first template</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(tmpl => (
            <div key={tmpl.id} className="bg-white border border-slate-200 rounded-sm p-5 hover:border-blue-300 hover:shadow-sm transition-all" data-testid={`template-card-${tmpl.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 bg-slate-50 border border-slate-200 rounded-sm flex items-center justify-center">
                  <FileCode2 size={20} className="text-blue-600" />
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-sm font-medium uppercase ${CATEGORY_COLORS[tmpl.category] || CATEGORY_COLORS.custom}`}>
                  {tmpl.category}
                </span>
              </div>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">{tmpl.name}</h3>
              {tmpl.description && <p className="text-xs text-slate-400 mb-2 line-clamp-2">{tmpl.description}</p>}
              {tmpl.variables?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {tmpl.variables.slice(0, 3).map(v => (
                    <span key={v} className="variable-chip text-[10px]">{`{{${v}}}`}</span>
                  ))}
                  {tmpl.variables.length > 3 && (
                    <span className="text-[10px] text-slate-400">+{tmpl.variables.length - 3} more</span>
                  )}
                </div>
              )}
              <p className="text-xs text-slate-400 mb-4">Created {formatDate(tmpl.created_at)}</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(`/templates/${tmpl.id}/use`)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-blue-700 text-white hover:bg-blue-800 rounded-sm text-xs font-medium transition-colors"
                  data-testid={`use-template-${tmpl.id}`}
                >
                  <Send size={12} /> Use
                </button>
                <button
                  onClick={() => navigate(`/templates/${tmpl.id}`)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-sm text-xs font-medium transition-colors"
                  data-testid={`edit-template-${tmpl.id}`}
                >
                  <Edit2 size={12} /> Edit
                </button>
                <button onClick={() => handleDelete(tmpl.id, tmpl.name)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" data-testid={`delete-template-${tmpl.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
